import React, { useEffect, useState } from "react";
import moment from "moment";

import { PrintIcon } from "../../../utils/TableActionIcons";
import {
    CreditInsuranceInvoiceColumns,
    exportCreditInsuranceInvoicesToExcelColumns,
    SummaryData,
} from "../../../constants/BillingConstants";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../shared/CustomPagination";
import CustomTable from "../../../shared/CustomTable";
import useFetchData from "../../../hooks/UseFetchData";
import BillingFilters from "./BillingFilters";
import { useTranslation } from "react-i18next";
import ExportInvoices from "./ExportInvoices";
import usePersistentBillingFilters from "../../../hooks/usePersistentBillingFilters";
import { useSelector } from "react-redux";
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from "../../../utils/businessLevelAccess";

function CreditNotesGovernmentInvoice() {
    const authUser = useSelector(state => state?.auth?.user);
    const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);

    const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('creditGovernment', { per_page: 10 });

    const [searchQuery, setSearchQuery] = useState(payload?.query || "");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data, loading, executeFetch, clearData } = useFetchData();

    const actionsCoumn = {
        body: actionsTemplate,
        header: t("Actions"),
    };

    const columnsData = [actionsCoumn, ...CreditInsuranceInvoiceColumns(t)];
    function actionsTemplate(rowData) {
        return (
            <div className="flex items-center gap-1">
                <PrintIcon
                    title={t("PrintInvoice")}
                    iconSize="20"
                    onClick={() =>
                        navigate(`/print-invoice/${rowData?.invoice_number}`, {
                            state: {
                                invoiceNumber: rowData?.invoice_number,
                                activeTabIndex: 3,
                                activeChildTabIndex: 0,
                                cancelInsurance: true,
                            },
                        })
                    }
                />
            </div>
        );
    }
    const onPageChange = async (newPage, newRowsPerPage) => {
        setPayload((prevPayload) => ({
            ...prevPayload,
            page: newPage,
            per_page: newRowsPerPage,
        }));
    };

    const fetchInitialData = (payloadData = appliedPayload) => {
        const formData = {
            ...payloadData,
            filters: { ...payloadData.filters }
        }
        formData.filters.credit_insurance_invoice_number = payloadData?.filters?.invoice_number
        delete formData?.filters.invoice_number
        executeFetch("POST", "/v3/invoices/listCancelGovernment", omitBillingDatesForSkipInitialLoad(authUser, formData));
    };

    // Consolidate initial load and pagination change
    useEffect(() => {
        if (skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)) return;
        fetchInitialData();
    }, [appliedPayload, skipInitialBillingLoad]);

    return (
        <div>
            {data?.data?.length > 0 && !loading && (
                <div className="mb-4 summary-container p-4 border rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                        {SummaryData(data, t)?.map((stat, index) => (
                            <div
                                key={index}
                                className={`${stat.color} rounded-lg p-2  border border-gray-100 hover:shadow transition-all`}
                            >
                                <div className="flex items-center">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {stat.label}
                                        </p>
                                        <p
                                            className={`text-xl font-bold mt-1 ${stat.color
                                                .replace("bg-", "text-")
                                                .replace("100", "600")}`}
                                        >
                                            {stat.value}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-col xl:flex-row xl:justify-between mb-2 xl:mb-0">
                <BillingFilters
                    applyFilters={applyFilters}
                    payloadData={payload}
                    setPayloadData={setPayload}
                    refetch={executeFetch}
                    clearData={clearData}
                    invoiceType="creditGovernment"
                    setSearchQuery={setSearchQuery}
                    fetchInitialData={fetchInitialData}
                    searchQuery={searchQuery}
                    resetFilters={() => {
                        resetPayload();
                        setSearchQuery("");
                    }}
                />
                <ExportInvoices
                    payload={{
                        start_date: payload?.start_date,
                        end_date: payload?.end_date,
                        query: payload?.query,
                        lang: "en",
                    }}
                    apiUrl={"/v3/invoices/exportCancelCompanyInoviceExcel"}
                    exportOptionsFormat={exportCreditInsuranceInvoicesToExcelColumns}
                    name={"credit_notes_government_invoices"}
                />
            </div>
            <CustomTable
                data={data?.data || []}
                columns={columnsData}
                isLoading={loading}
            />
            {data?.data?.length > 0 && (
                <CustomPagination
                    currentPage={data?.page}
                    rowsPerPage={data?.per_page}
                    totalRecords={data?.totalRecords}
                    totalPages={data?.totalPages}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
}

export default CreditNotesGovernmentInvoice;
